import { Fragment } from 'react';

import { Link } from '@tanstack/react-router';

import { useAuth } from '@/auth/useAuth';
import {
  Avatar,
  Icon,
  IconButton,
  MenuDivider,
  MenuItem,
  MenuSectionLabel,
  Popover,
  initialsOf,
} from '@/components/ui';

import { useBreadcrumbs } from './breadcrumbs';
import { JobsIndicator } from './JobsIndicator';

interface TopBarProps {
  onMenuClick: () => void;
}

export function TopBar({ onMenuClick }: TopBarProps) {
  const crumbs = useBreadcrumbs();

  return (
    <div className="app-topbar">
      <IconButton
        icon="menu"
        label="Open navigation"
        onClick={onMenuClick}
        className="lg:hidden"
      />

      <nav className="crumbs" aria-label="Breadcrumb">
        {crumbs.map((c, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <Fragment key={`${c.label}-${i}`}>
              {i > 0 && <span className="crumb-sep">/</span>}
              {isLast || !c.to ? (
                <span className="crumb-current">{c.label}</span>
              ) : (
                <Link to={c.to} className="crumb-link">
                  {c.label}
                </Link>
              )}
            </Fragment>
          );
        })}
      </nav>

      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
        <JobsIndicator />
        <UserMenu />
      </div>
    </div>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const display =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.username ?? user?.email ?? 'Unknown user';
  const initials = initialsOf(user?.firstName ?? user?.username, user?.lastName);

  return (
    <Popover
      placement="bottom-end"
      trigger={
        <button
          type="button"
          className="ds-icon-btn"
          aria-label={`Account: ${display}`}
          style={{ width: 32, height: 32, padding: 0, marginLeft: 'auto' }}
        >
          <Avatar initials={initials} size={28} />
        </button>
      }
    >
      <MenuSectionLabel>{display}</MenuSectionLabel>
      {user?.email && (
        <div style={{ padding: '0 8px 6px', fontSize: 11.5, color: 'var(--text-3)' }}>
          {user.email}
        </div>
      )}
      <MenuDivider />
      <MenuItem icon={<Icon name="logout" size={14} />} onClick={() => void logout()}>
        Sign out
      </MenuItem>
    </Popover>
  );
}
